import { DocumentNode, NetworkStatus, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box, ToastType } from '@bechtel/gds';
import {
  OperatorType,
  Package,
  PackageCommodity,
  PackageCommodityType,
  SaveCommodityPackagesInput,
  SaveCommodityPackagesResult,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { filter, find, get, includes, isEmpty, isEqual, map } from 'lodash';
import { FC, lazy, useCallback, useMemo, useRef, useState } from 'react';
import { ErrorRetry, GridSkeleton, useAppUser } from '~/components';
import {
  GET_CABLE_VIEW_STATS,
  GET_EQUIPMENT_VIEW_STATS,
  GET_JUMPER_VIEW_STATS,
  GET_PACKAGES_BY_COMMODITY,
  GET_RACEWAY_VIEW_STATS,
  SAVE_COMMODITY_PACKAGES,
  SEARCH_PACKAGES,
} from '~/gql';
import { usePermissions, useToast } from '~/hooks';
import { CommodityFooter } from '../commodity-footer';
import { ClientGrid } from '../grid';
import { CommodityPackageProps } from './types';
const MessageModal = lazy(() => import('~/components/organisms/message-modal/message'));

const getPackageCommodityPermission = (
  commodityType: string,
  canUpdateEquipment: boolean,
  canUpdateRaceway: boolean,
  canUpdateJumper: boolean,
  canUpdateCable: boolean
) => {
  switch (commodityType) {
    case PackageCommodityType.Equipment:
      return canUpdateEquipment;
    case PackageCommodityType.Raceway:
      return canUpdateRaceway;
    case PackageCommodityType.Jumper:
      return canUpdateJumper;
    case PackageCommodityType.Cable:
      return canUpdateCable;
    default:
      return false;
  }
};

export const CommodityPackage: FC<CommodityPackageProps> = ({
  tableName,
  systemLayoutName,
  commodityId,
  commodityType,
}) => {
  const [packageCommodities, setPackageCommodities] = useState<PackageCommodity[]>([]);
  const [initialPackageCommodities, setInitialPackageCommodities] = useState<PackageCommodity[]>([]);
  const { showToast } = useToast();
  const { canUpdateEquipment, canUpdateRaceway, canUpdateJumper, canUpdateCable } = usePermissions();
  const searchPackagesRef = useRef<any>(null);
  const { userAccount } = useAppUser();
  const canUpdateCommodityPackage = getPackageCommodityPermission(
    commodityType,
    canUpdateEquipment,
    canUpdateRaceway,
    canUpdateJumper,
    canUpdateCable
  );
  const [searchPackages] = useLazyQuery(SEARCH_PACKAGES);
  const {
    loading: packageCommodityLoading,
    error: packageCommodityError,
    refetch: packageCommodityRefetch,
    networkStatus: equipmentPackagesNetworkStatus,
  } = useQuery<{
    packagesByCommodity: [PackageCommodity];
  }>(GET_PACKAGES_BY_COMMODITY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      commodityId: commodityId,
      packageCommodityType: commodityType,
    },
    onCompleted: (data) => {
      if (data.packagesByCommodity?.length) {
        setPackageCommodities(data.packagesByCommodity);
        setInitialPackageCommodities(data.packagesByCommodity);
      }
    },
  });

  const onEdited = useCallback(
    (prevPackageCommodities, editedRow) => {
      //tag can be picked from picklist or grid column or on datepicker change
      const tag = editedRow['package.tag'] || editedRow?.package?.tag;
      const index = prevPackageCommodities?.findIndex((item) => item?.id === editedRow.id);

      const packageObj: Package =
        searchPackagesRef.current && searchPackagesRef.current.length > 0
          ? searchPackagesRef.current[0]
          : editedRow.package;
      const newPackageCommodity: PackageCommodity = {
        ...editedRow,
        id: editedRow.id ?? crypto.randomUUID(),
        package: { ...packageObj },
        commodityId: commodityId,
        packageId: packageObj?.id,
        projectId: packageObj?.projectId ?? userAccount?.defaultProjectId,
        updatedTime: new Date(),
        updatedBy: '',
      };
      const updatedIndex = index === -1 ? prevPackageCommodities.length : index;
      const packageCommodities = [...prevPackageCommodities];
      if (editedRow.dateChanged) {
        packageCommodities[updatedIndex] = { ...editedRow };
        return [...packageCommodities];
      }
      if (tag) {
        packageCommodities[updatedIndex] = { ...newPackageCommodity };
        return [...packageCommodities];
      }
      searchPackagesRef.current = null;
      return packageCommodities;
    },
    [commodityId, userAccount?.defaultProjectId]
  );

  const onPackageCommodityEdited = useCallback(
    (editedRow: PackageCommodity) => {
      setPackageCommodities((prev) => onEdited(prev, editedRow));
    },
    [onEdited, setPackageCommodities]
  );

  const onDeleted = useCallback((deletedIDs: string[]) => {
    setPackageCommodities((prev) => filter(prev, (item) => !includes(deletedIDs, item.id)));
  }, []);

  const getQueryName = (): DocumentNode => {
    switch (commodityType) {
      case PackageCommodityType.Cable:
        return GET_CABLE_VIEW_STATS;
      case PackageCommodityType.Equipment:
        return GET_EQUIPMENT_VIEW_STATS;
      case PackageCommodityType.Raceway:
        return GET_RACEWAY_VIEW_STATS;
      case PackageCommodityType.Jumper:
        return GET_JUMPER_VIEW_STATS;
      case PackageCommodityType.CableFromTerm:
        return GET_CABLE_VIEW_STATS;
      case PackageCommodityType.CableToTerm:
        return GET_CABLE_VIEW_STATS;
      default:
        return GET_CABLE_VIEW_STATS;
    }
  };

  const [saveCommodityPackages, { data: saveCommodityPackagesData, loading: saveCommodityPackagesLoading }] =
    useMutation<
      { saveCommodityPackages: SaveCommodityPackagesResult },
      { saveCommodityPackagesInput: SaveCommodityPackagesInput }
    >(SAVE_COMMODITY_PACKAGES, {
      onCompleted: (data) => {
        if (data) {
          const updatedCommodityPackages = get(data, 'saveCommodityPackages.commodityPackages');
          if (updatedCommodityPackages) {
            showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, ['Package(s)', ''], ToastType.INFO);
          }
        }
      },
      onError: () => {
        showToast(UiTextEnum.ERROR_WHILE_UPDATING_TOAST, ['Package(s)', ''], ToastType.INFO);
      },
      update: (cache, { data }) => {
        const updatedCommodityPackages = get(data, 'saveCommodityPackages.commodityPackages');
        if (updatedCommodityPackages) {
          cache.writeQuery({
            query: GET_PACKAGES_BY_COMMODITY,
            data: { packagesByCommodity: updatedCommodityPackages },
            variables: {
              commodityId: commodityId,
              packageCommodityType: commodityType,
            },
          });
        }
      },
      refetchQueries: [
        {
          query: getQueryName(),
          variables: {
            id: commodityId,
          },
        },
      ],
    });

  const onSaveCommodityPackages = useCallback(() => {
    saveCommodityPackages({
      variables: {
        saveCommodityPackagesInput: {
          commodityPackages: map(packageCommodities, (item) => {
            return {
              packageId: item.package?.id || '',
              assignedDate: item.assignedDate,
              asBuiltDate: item.asBuiltDate,
            };
          }),
          packageCommodityType: commodityType,
          commodityId: commodityId,
        },
      },
    });
  }, [saveCommodityPackages, packageCommodities, commodityType, commodityId]);

  const errorKeys: UiMessageKey[] =
    get(saveCommodityPackagesData, 'saveCommodityPackages.infos') ??
    get(saveCommodityPackagesData, 'saveCommodityPackages.errors') ??
    [];

  const validateTag = useCallback(
    async (value, isPicked) => {
      const existingRow = find(packageCommodities, (item) => item?.package?.tag === value);
      searchPackagesRef.current = null;
      if (isPicked) {
        return isEmpty(existingRow);
      } else {
        const { data } = await searchPackages({
          variables: {
            searchInput: {
              filterInput: {
                and: {
                  field: 'tag',
                  operator: OperatorType.EQUAL,
                  value: value,
                },
              },
            },
          },
        });
        searchPackagesRef.current = data?.searchPackages?.items;
        return data.searchPackages.items.length > 0 && isEmpty(existingRow);
      }
    },
    [packageCommodities, searchPackages]
  );

  const validationFns = useMemo(() => {
    return {
      packageId: async (value, api, isPicked) => {
        return validateTag(value, isPicked);
      },
    };
  }, [validateTag]);

  if (packageCommodityLoading || equipmentPackagesNetworkStatus === NetworkStatus.refetch) {
    return <GridSkeleton />;
  }

  if (packageCommodityError) {
    return <ErrorRetry refetch={packageCommodityRefetch} />;
  }

  return (
    <>
      <Box flexGrow={{ sm: ' 1' }} flexDirection={{ sm: 'column' }}>
        <ClientGrid
          rowSelection="multiple"
          getRowId={(data) => data.id}
          name={tableName}
          systemLayoutName={systemLayoutName}
          rowData={packageCommodities}
          onRowEdited={onPackageCommodityEdited}
          onRowsDeleted={onDeleted}
          originalRowData={initialPackageCommodities}
          readOnly={!canUpdateCommodityPackage}
          validationFns={validationFns}
          picklistKeys={{ packageId: { selectKey: 'tag', returnKey: 'package', expectsObj: true } }}
        ></ClientGrid>
        <CommodityFooter
          uiType="Grid"
          saveHandler={onSaveCommodityPackages}
          loading={saveCommodityPackagesLoading}
          disableSave={!canUpdateCommodityPackage || isEqual(packageCommodities, initialPackageCommodities)}
          disableCancel={isEqual(packageCommodities, initialPackageCommodities)}
          cancelHandler={() => setPackageCommodities([...initialPackageCommodities])}
        />
      </Box>
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
    </>
  );
};
